import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export type ModuleId =
  | "dashboard"
  | "configuration"
  | "masters"
  | "purchasing"
  | "sales"
  | "materials"
  | "material-unit-conversions"
  | "material-groups"
  | "plant-types"
  | "plants"
  | "storage-locations"
  | "storage-loc-templates"
  | "batches"
  | "batch-templates"
  | "companies"
  | "units"
  | "cost-centers"
  | "production-orders"
  | "approval-flows"
  | "movement-types"
  | "vendors"
  | "commercial-business-lines"
  | "vendor-services"
  | "customers"
  | "payment-terms"
  | "document-classes"
  | "purchase-orders"
  | "sales-orders"
  | "carrier-types"
  | "carriers"
  | "drivers"
  | "vehicles"
  | "vehicle-assignments"
  | "trips"
  | "routes"
  | "agricola"
  | "fields"
  | "employees"
  | "cuarteles"
  | "attendance"
  | "labor-tickets"
  | "labor-activities"
  | "labor-rates"
  | "labor-rate-generals"
  | "especies"
  | "variedades"
  | "labor-retention"
  | "mm"
  | "material-movements"
  | "work-orders"
  | "work-order-templates"
  | "work-order-activities"
  | "industrial-formulas"
  | "dispatch-guides"
  | "dispatch-guide-statuses"
  | "dispatch-flow-profiles"
  | "stock"
  | "material-requests"
  | "material-requests-approval"
  | "material-requests-warehouse"
  | "reports"
  | "users"
  | "org-positions"
  | "roles"
  | "permissions"
  | "settings"
  | "subscriptions"
  | "plans"
  | "plan-prices"
  | "invoices"
  | "currencies"
  | "units-core"
  | "audit";

export type ParentModuleId =
  | "configuration"
  | "masters"
  | "purchasing"
  | "sales"
  | "mm"
  | "logistica"
  | "agricola"
  | "industrial"
  | "material-requests"
  | "reports"
  | "users-section"
  | "settings";

interface NavigationState {
  currentModule: ModuleId;
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
}

// Mapeo de módulos a rutas
const moduleRoutes: Record<ModuleId, string> = {
  dashboard: "/dashboard",
  configuration: "/cost-centers",
  masters: "/materials", // Default to materials when masters is selected
  purchasing: "/purchase-orders",
  sales: "/sales-orders",
  materials: "/materials",
  "material-unit-conversions": "/material-unit-conversions",
  "material-groups": "/material-groups",
  "plant-types": "/plant-types",
  plants: "/plants",
  "storage-locations": "/storage-locations",
  "storage-loc-templates": "/storage-loc-templates",
  batches: "/batches",
  "batch-templates": "/batch-templates",
  companies: "/companies",
  units: "/units",
  "cost-centers": "/cost-centers",
  "production-orders": "/production-orders",
  "approval-flows": "/approval-flows",
  "movement-types": "/movement-types",
  vendors: "/vendors",
  "commercial-business-lines": "/commercial-business-lines",
  "vendor-services": "/vendor-services",
  customers: "/customers",
  "payment-terms": "/payment-terms",
  "document-classes": "/document-classes",
  "purchase-orders": "/purchase-orders",
  "sales-orders": "/sales-orders",
  "carrier-types": "/carrier-types",
  carriers: "/carriers",
  drivers: "/drivers",
  vehicles: "/vehicles",
  "vehicle-assignments": "/vehicle-assignments",
  trips: "/trips",
  routes: "/routes",
  agricola: "/fields",
  fields: "/fields",
  cuarteles: "/cuarteles",
  employees: "/employees",
  attendance: "/attendance",
  "labor-tickets": "/labor-tickets",
  "labor-activities": "/labor-activities",
  "labor-rates": "/labor-rates",
  "labor-rate-generals": "/labor-rate-generals",
  especies: "/especies",
  variedades: "/variedades",
  "labor-retention": "/labor-retention",
  mm: "/material-movements",
  "material-movements": "/material-movements",
  "work-orders": "/work-orders",
  "work-order-templates": "/work-order-templates",
  "work-order-activities": "/work-order-activities",
  "industrial-formulas": "/industrial-formulas",
  "dispatch-guides": "/dispatch-guides",
  "dispatch-guide-statuses": "/dispatch-guide-statuses",
  "dispatch-flow-profiles": "/dispatch-flow-profiles",
  stock: "/stock",
  "material-requests": "/material-requests",
  "material-requests-approval": "/material-requests",
  "material-requests-warehouse": "/material-requests",
  reports: "/reports",
  users: "/users",
  "org-positions": "/organization-positions",
  roles: "/roles",
  permissions: "/permissions",
  settings: "/settings",
  subscriptions: "/subscriptions",
  plans: "/plans",
  "plan-prices": "/plan-prices",
  invoices: "/invoices",
  currencies: "/currencies",
  "units-core": "/units-core",
  audit: "/audit",
};

export function useNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialCollapsed = (): boolean => {
    const stored = localStorage.getItem("sidebarCollapsed");
    if (stored !== null) return stored === "true";
    return true;
  };

  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentModule: "mm",
    sidebarOpen: false,
    sidebarCollapsed: getInitialCollapsed(),
  });

  // Persistir sidebarCollapsed en localStorage
  useEffect(() => {
    localStorage.setItem(
      "sidebarCollapsed",
      String(navigationState.sidebarCollapsed),
    );
  }, [navigationState.sidebarCollapsed]);

  // Determinar el módulo actual basado en la ruta
  useEffect(() => {
    const path = location.pathname;

    // Ignoramos los alias de módulos padre que comparten ruta con sus hijos
    const parentAliases = [
      "masters",
      "mm",
      "purchasing",
      "sales",
      "configuration",
    ];
    const validEntries = Object.entries(moduleRoutes).filter(
      ([key]) => !parentAliases.includes(key),
    );

    // Ordenamos por longitud de ruta (descendente) para que las rutas más específicas coincidan primero
    validEntries.sort((a, b) => b[1].length - a[1].length);

    const currentModule =
      (validEntries.find(([, route]) => {
        // Coincidencia exacta
        if (route === path) return true;
        // Rutas que empiezan con el path (para rutas anidadas como /carriers/new)
        if (path.startsWith(route + "/")) return true;
        return false;
      })?.[0] as ModuleId) || "mm";

    setNavigationState((prev) => ({
      ...prev,
      currentModule,
    }));
  }, [location.pathname]);

  const setCurrentModule = (module: ModuleId) => {
    const route = moduleRoutes[module];
    if (route) {
      navigate(route);
    }
    setNavigationState((prev) => ({
      ...prev,
      currentModule: module,
    }));
  };

  const toggleSidebar = () => {
    setNavigationState((prev) => ({
      ...prev,
      sidebarOpen: !prev.sidebarOpen,
    }));
  };

  const closeSidebar = () => {
    setNavigationState((prev) => ({
      ...prev,
      sidebarOpen: false,
    }));
  };

  const toggleSidebarCollapse = () => {
    setNavigationState((prev) => ({
      ...prev,
      sidebarCollapsed: !prev.sidebarCollapsed,
    }));
  };

  const expandSidebar = () => {
    setNavigationState((prev) => ({
      ...prev,
      sidebarCollapsed: false,
    }));
  };

  const collapseSidebar = () => {
    setNavigationState((prev) => ({
      ...prev,
      sidebarCollapsed: true,
    }));
  };

  const getParentModule = (moduleId: ModuleId): ParentModuleId => {
    const configurationSubModules = [
      "cost-centers",
      "plant-types",
      "companies",
      "labor-retention",
      "approval-flows",
    ];
    const purchasingSubModules = [
      "purchase-orders",
      "payment-terms",
      "document-classes",
      "vendors",
      "vendor-services",
      "material-requests",
      "material-requests-approval",
      "material-requests-warehouse",
    ];
    const salesSubModules = ["sales-orders", "customers"];
    const usersSubModules = ["users", "org-positions", "roles", "permissions"];
    const mmSubModules = [
      "material-movements",
      "dispatch-guides",
      "dispatch-guide-statuses",
      "dispatch-flow-profiles",
      "stock",
      "materials",
      "material-groups",
      "batches",
      "batch-templates",
      "units",
      "plants",
      "storage-locations",
      "storage-loc-templates",
      "movement-types",
    ];
    const agricolaSubModules = [
      "labor-tickets",
      "fields",
      "cuarteles",
      "employees",
      "attendance",
      "labor-activities",
      "labor-rates",
      "labor-rate-generals",
      "especies",
      "variedades",
    ];
    const logisticaSubModules = [
      "trips",
      "routes",
      "carriers",
      "drivers",
      "vehicles",
      "vehicle-assignments",
      "carrier-types",
      "dispatch-flow-profiles",
    ];
    const industrialSubModules = [
      "work-orders",
      "work-order-templates",
      "work-order-activities",
      "industrial-formulas",
      "production-orders",
    ];

    if (configurationSubModules.includes(moduleId)) {
      return "configuration";
    }
    if (purchasingSubModules.includes(moduleId)) {
      return "purchasing";
    }
    if (salesSubModules.includes(moduleId)) {
      return "sales";
    }
    if (usersSubModules.includes(moduleId)) {
      return "users-section";
    }
    if (mmSubModules.includes(moduleId)) {
      return "mm";
    }
    if (agricolaSubModules.includes(moduleId)) {
      return "agricola";
    }
    if (logisticaSubModules.includes(moduleId)) {
      return "logistica";
    }
    if (industrialSubModules.includes(moduleId)) {
      return "industrial";
    }
    return moduleId as ParentModuleId;
  };

  // Cerrar sidebar en pantallas grandes automáticamente
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setNavigationState((prev) => ({
          ...prev,
          sidebarOpen: false,
        }));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    currentModule: navigationState.currentModule,
    sidebarOpen: navigationState.sidebarOpen,
    sidebarCollapsed: navigationState.sidebarCollapsed,
    setCurrentModule,
    toggleSidebar,
    closeSidebar,
    toggleSidebarCollapse,
    expandSidebar,
    collapseSidebar,
    getParentModule,
  };
}
